<template>
  <!--
    「요금·결제」의 지금 청구 창(10-30 운영 관리 — SQL 103) — 등록된 카드로 지금 결제한다. 청구할 때가 됐는지·기간·금액은 서버가 매일
    회차와 같은 판정으로 정한다(아직이면 409 `NOT_DUE`) — 이 창은 서버 값(`price`)을 옮기고 결과를 말로 옮길 뿐이다. 되돌리기는 청구
    내역의 [청구 취소]. 말·요청은 `utils/subscriptionAdmin` 한 곳.
  -->
  <el-dialog
    :title="$t(text.chargeNow)"
    :visible="visible"
    width="440px"
    append-to-body
    data-testid="subscription-charge-now-dialog"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
    @open="reset">
    <p class="subscription-charge-now__store">{{ storeLabel }}</p>
    <dl class="subscription-charge-now__rows" data-testid="subscription-charge-now-rows">
      <template v-for="row in rows">
        <dt :key="`${row.label}-dt`">{{ $t(row.label) }}</dt>
        <dd :key="`${row.label}-dd`">{{ row.value }}</dd>
      </template>
    </dl>
    <p class="subscription-charge-now__intro">{{ $t(text.chargeNowIntro) }}</p>
    <p v-if="retryNotice" class="subscription-charge-now__retry" data-testid="subscription-charge-now-retry">
      {{ $t(retryNotice) }}
    </p>
    <p v-if="problem" class="subscription-charge-now__problem" data-testid="subscription-charge-now-problem">
      {{ problem }}
    </p>
    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ $t(text.close) }}</el-button>
      <el-button
        size="small"
        type="primary"
        data-testid="subscription-charge-now-submit"
        :loading="busy"
        @click="submit"
        >{{ $t(text.chargeNowSubmit) }}</el-button
      >
    </span>
  </el-dialog>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { httpErrorMessage } from '@/plugins/http/policy'
import {
  SUBSCRIPTION_TEXT,
  SUBSCRIPTION_WRITE_CONFIG,
  chargeNowRowsOf,
  chargeOutcomeOf,
  chargeRequestOf,
  chargeRetryNoticeOf,
  notDueTextOf,
} from '@/utils/subscriptionAdmin'

export default {
  name: 'SubscriptionChargeNowDialog',

  mixins: [mainVueMixin],

  props: {
    visible: { type: Boolean, default: false },
    /** 요청할 구독 — 상세 창이 가진 ID(상세를 다시 읽는 동안·읽기 실패에도 그대로다) */
    subscriptionId: { type: Number, default: null },
    /** 상세(`GET /api/admin/subscriptions/{id}`) — 그리기만 한다. 다시 읽는 동안은 `null` */
    detail: { type: Object, default: null },
    /** 매장 이름(「QA 피자 · S2600…」) */
    storeLabel: { type: String, default: '' },
  },

  data: function () {
    return {
      text: SUBSCRIPTION_TEXT,
      /**
       * 보내지 못한 거절의 원본(`HttpError` — 코드·서버 문장). 문장은 [problem] 이 셈한다 — 「아직 때가 아님」의 다음 결제 날짜는
       * 다시 읽은 상세로 바뀌어야 하고, 상세가 잠시 비어도 서버 문장은 남아야 한다
       */
      failure: null,
      busy: false,
    }
  },

  computed: {
    /** 창 안에서 한 번 말할 문장 — 409 `NOT_DUE` 는 지금 상세의 다음 결제 날짜를 붙여서, 그 밖은 서버 문장 그대로 */
    problem: function () {
      if (!this.failure) return ''

      return notDueTextOf(this.failure, this.detail) || httpErrorMessage(this.failure, this.$t(this.text.writeFailed))
    },
    rows: function () {
      return chargeNowRowsOf(this.detail)
    },
    retryNotice: function () {
      return chargeRetryNoticeOf(this.detail)
    },
  },

  methods: {
    /** 열 때마다 새로 — 앞 창의 문장을 비운다 */
    reset: function () {
      this.failure = null
      this.busy = false
    },

    /**
     * 결제하기 — 헥토로 보냈으면(승인·거절·답 없음 모두 200) 그 결과를 한 줄로 말하고 닫은 뒤 부모가 상세·목록을 다시 읽게 한다. 보내지
     * 않은 거절은 창 안에서 말한다: 409 `NOT_DUE` 는 다음 결제 날짜를 붙여서, 그 밖(409 `NOT_RECURRING`·`CARD_REQUIRED`·
     * `CHARGE_PENDING` · 503 `CHARGE_OFF`·`NOT_CONFIGURED`)은 서버 문장 그대로. 409 면 그 사이 바뀐 것일 수 있어 다시 읽는다.
     */
    submit: async function () {
      const request = chargeRequestOf(this.subscriptionId)

      this.busy = true
      this.failure = null
      try {
        const response = await this.$axios.post(request.url, request.body, SUBSCRIPTION_WRITE_CONFIG)
        const outcome = chargeOutcomeOf(response && response.data)

        this.$emit('update:visible', false)
        this.mainVue.$message({ type: outcome.type, message: this.$t(outcome.message) })
        this.$emit('changed')
      } catch (error) {
        this.failure = error
        if (error && error.status === 409) this.$emit('changed')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-charge-now__store {
  margin: 0 0 8px;
  color: var(--color-text-regular);
}

.subscription-charge-now__rows {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 4px 16px;
  margin: 0 0 12px;
}

.subscription-charge-now__rows dt {
  color: var(--color-text-secondary);
}

.subscription-charge-now__rows dd {
  margin: 0;
  color: var(--color-text-primary);
}

.subscription-charge-now__intro {
  margin: 0 0 8px;
  color: var(--color-text-regular);
  word-break: keep-all;
}

.subscription-charge-now__retry,
.subscription-charge-now__problem {
  margin: 0 0 8px;
  color: var(--color-danger);
  word-break: keep-all;
}
</style>
